import {
    createContext,
    forwardRef,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { LngLatBounds } from '@yandex/ymaps3-types';
import throttle from 'lodash/throttle';
import type { Nullable, Optional } from '@direct-frontend/stdlib';
import {
    getPickupPoint,
    getSelfPickupPoint,
    useLazyPickupPoints,
    useLazySelfPickupPoints,
} from '@/entities/pickupPoint';
import { trackGoal } from '@/entities/trackGoal';
import type { ServiceType } from '@/shared/api/order';
import { type PickupPoint, PickupPointPaymentMethods } from '@/shared/api/pickupPoint';
import { getRequestContext } from '@/shared/lib/requestContext';
import { formatSchedule, usePickupFilters, useSelfPickupFilters } from './lib';

const THROTTLE_TIMEOUT = 1_500;

type PickupAddressPickerContextPayload = {
    type: 'self' | 'pickup';
    pickupPoint: Nullable<PickupPoint>;
    pickupPoints: PickupPoint[];
    allItemsCount: Optional<number>;
    onSelectPickupPoint: (pickupPoint: Nullable<PickupPoint>) => void;
    onUpdateBounds: (bounds: LngLatBounds) => void;
} & ReturnType<typeof usePickupFilters> &
    Omit<ReturnType<typeof useSelfPickupFilters>, 'points'>;

const PickupAddressContext = createContext<Nullable<PickupAddressPickerContextPayload>>(null);

export function usePickupAddressContext(): PickupAddressPickerContextPayload {
    const context = useContext(PickupAddressContext);

    if (!context) {
        throw new Error('Hook "usePickupAddressContext" is called outside of PickupAddressContext');
    }

    return context;
}

const isBoundsIncludes = (left: LngLatBounds, right: LngLatBounds): boolean => {
    const [[leftLonFrom, leftLatTo], [leftLonTo, leftLatFrom]] = left;
    const [[rightLonFrom, rightLatTo], [rightLonTo, rightLatFrom]] = right;

    return (
        leftLonFrom <= rightLonFrom && leftLatFrom <= rightLatFrom && leftLatTo >= rightLatTo && leftLonTo >= rightLonTo
    );
};

interface PickupAddressProviderProps {
    storeSlug: string;
    pickupId?: string;
    children: ReactNode;
    orderId: string;
    type: 'self' | 'pickup';
    serviceType?: Optional<ServiceType>;
}

export const PickupAddressProvider = forwardRef(function PickupAddressProvider(props: PickupAddressProviderProps, ref) {
    const { orderId, pickupId: propsPickId, storeSlug, serviceType, type, children } = props;
    const {
        delivery: { multiWarehousesEnabled },
    } = getRequestContext();

    const boundsRef = useRef<LngLatBounds | null>(null);
    const [pickupId, setPickupId] = useState<string | null>(propsPickId || null);
    const [
        loadSelfPickupPoints,
        { data: { points: selfPickupPointsData = [], itemsCount: selfPickupItemsCount } = {} },
    ] = useLazySelfPickupPoints();
    const [loadPickupPoints, { data: pickupPoints = [] }] = useLazyPickupPoints();
    const [pickupPoint, setPickupPoint] = useState<PickupPoint | null>(null);

    const { pickupFilters, changePickupFilters } = usePickupFilters();
    const {
        selfPickupFilters,
        changeSelfPickupFilters,
        points: selfPickupPoints,
    } = useSelfPickupFilters({ points: selfPickupPointsData, allItemsCount: selfPickupItemsCount });

    const getPickupPointObject = useCallback(async () => {
        if (pickupId) {
            let activePickupPoint = null;

            if (type === 'pickup') {
                if (!pickupPoints.length && serviceType) {
                    activePickupPoint = await getPickupPoint({
                        targetId: pickupId,
                        storeSlug,
                        serviceType,
                        orderId,
                    });
                } else {
                    activePickupPoint = (pickupPoints || []).find((point) => point.id === pickupId) ?? null;
                }
            }

            if (type === 'self') {
                if (!selfPickupPoints.length && serviceType) {
                    activePickupPoint = await getSelfPickupPoint({
                        targetId: pickupId,
                        orderId,
                        storeSlug,
                    });
                } else {
                    activePickupPoint = (selfPickupPoints || []).find((point) => point.id === pickupId) ?? null;
                }
            }

            if (activePickupPoint) {
                setPickupPoint(activePickupPoint);
            }
        }
    }, [orderId, pickupId, pickupPoints, selfPickupPoints, serviceType, storeSlug, type]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Автоматический игнор в рамках DIRECT-288723
        getPickupPointObject();
    }, [getPickupPointObject]);

    const loadPoints = useMemo(
        () =>
            throttle((bounds: LngLatBounds) => {
                const [[lonFrom, latTo], [lonTo, latFrom]] = bounds;
                const deltaLat = lonTo - lonFrom;
                const deltaLon = latTo - latFrom;

                if (type === 'pickup') {
                    loadPickupPoints({
                        order_id: orderId,
                        store_slug: storeSlug,
                        type: 'pickup_point',
                        longitudeFrom: lonFrom - deltaLat / 2,
                        longitudeTo: lonTo + deltaLat / 2,
                        latitudeFrom: latFrom - deltaLon / 2,
                        latitudeTo: latTo + deltaLon / 2,
                        ...pickupFilters,
                    });
                }

                if (type === 'self') {
                    loadSelfPickupPoints({
                        order_id: orderId,
                        store_slug: storeSlug,
                        type: 'pickup_point',
                        longitudeFrom: lonFrom - deltaLat / 2,
                        longitudeTo: lonTo + deltaLat / 2,
                        latitudeFrom: latFrom - deltaLon / 2,
                        latitudeTo: latTo + deltaLon / 2,
                        multiWarehousesEnabled,
                    });
                }
            }, THROTTLE_TIMEOUT),
        [type, loadPickupPoints, orderId, storeSlug, pickupFilters, loadSelfPickupPoints, multiWarehousesEnabled]
    );

    useEffect(() => {
        if (boundsRef.current) {
            loadPoints(boundsRef.current);
        }
    }, [loadPoints]);

    const onUpdateBounds = useMemo(
        () => (bounds: LngLatBounds) => {
            if (
                type === 'pickup' &&
                pickupPoints.length > 0 &&
                boundsRef.current &&
                isBoundsIncludes(boundsRef.current, bounds)
            ) {
                return;
            }

            if (
                type === 'self' &&
                selfPickupPoints.length > 0 &&
                boundsRef.current &&
                isBoundsIncludes(boundsRef.current, bounds)
            ) {
                return;
            }

            boundsRef.current = bounds;

            loadPoints(bounds);
        },
        [type, pickupPoints.length, selfPickupPoints.length, loadPoints]
    );

    useImperativeHandle(ref, () => {
        return {
            reset() {
                setPickupId(propsPickId || null);

                if (!propsPickId) {
                    setPickupPoint(null);
                }
            },
        };
    });

    const payload: PickupAddressPickerContextPayload = {
        type,
        pickupPoint,
        pickupPoints: type === 'self' ? selfPickupPoints : pickupPoints,
        allItemsCount: type === 'self' ? selfPickupItemsCount : undefined,
        pickupFilters,
        selfPickupFilters,
        onUpdateBounds,
        changePickupFilters,
        changeSelfPickupFilters,
        onSelectPickupPoint: (point: Nullable<PickupPoint>) => {
            if (point?.id) {
                trackGoal('pickup_point_clicked', {
                    cash_available: point.paymentMethods.includes(PickupPointPaymentMethods.CASH_ON_RECEIPT),
                    card_prepaid_available: point.paymentMethods.includes(PickupPointPaymentMethods.ALREADY_PAID),
                    card_postpaid_available: point.paymentMethods.includes(PickupPointPaymentMethods.CARD_ON_RECEIPT),
                    working_times: formatSchedule(point.schedule).join(' '),
                });
                setPickupId(point?.id);
            } else {
                setPickupId(null);
                setPickupPoint(null);
            }
        },
    };

    return <PickupAddressContext.Provider value={payload}>{children}</PickupAddressContext.Provider>;
});
